import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { EditableFilterRow, FilterConnector } from '~/types/filters';
import { FilterDropdown } from './FilterDropdown';
import {
  FilterRow,
  type FilterFieldOption,
  type FilterValueInputRenderer,
} from './FilterRow';

type FilterRowsPanelProps = {
  readonly namespace: string;
  /** Prefix of the `title`, `clear_all`, `add`, `none`, `none_hint`, `where`, `and`, `or` and `remove` keys. */
  readonly keyPrefix: string;
  readonly rows: EditableFilterRow[];
  readonly fieldOptions: FilterFieldOption[];
  readonly newRow: () => EditableFilterRow;
  readonly initialValueFor: (field: string) => string | string[];
  readonly valueInputFor: (
    field: string
  ) => FilterValueInputRenderer | undefined;
  readonly isAlwaysAndRow?: (row: EditableFilterRow) => boolean;
  readonly maxHeight?: number;
  readonly topContent?: ReactNode;
  readonly bottomContent?: ReactNode;
  readonly onRowsChange: (rows: EditableFilterRow[]) => void;
  readonly onClearAll?: () => void;
};

/**
 * The editable list of filter rows inside a filter popover: add, edit, remove and clear.
 *
 * @example
 * <FilterRowsPanel
 *   namespace="inventory"
 *   keyPrefix="inventory.buy_list.filter"
 *   rows={rows}
 *   fieldOptions={fieldOptions}
 *   newRow={newBuyListFilterRow}
 *   initialValueFor={(field) => (field === 'platformProductIds' ? [] : '')}
 *   valueInputFor={(field) =>
 *     field === 'platformProductIds' ? productValueInput : undefined
 *   }
 *   onRowsChange={setRows}
 * />
 */
export function FilterRowsPanel({
  namespace,
  keyPrefix,
  rows,
  fieldOptions,
  newRow,
  initialValueFor,
  valueInputFor,
  isAlwaysAndRow = () => false,
  maxHeight,
  topContent,
  bottomContent,
  onRowsChange,
  onClearAll = () => onRowsChange([]),
}: FilterRowsPanelProps) {
  const { t } = useTranslation(namespace);
  const firstChainedRowIndex = rows.findIndex((row) => !isAlwaysAndRow(row));

  const fixedConnectorLabelFor = (row: EditableFilterRow, index: number) => {
    if (index === 0) return undefined;
    if (isAlwaysAndRow(row)) return t(`${keyPrefix}.and`);
    if (index === firstChainedRowIndex) return t(`${keyPrefix}.where`);
    return undefined;
  };

  const replaceRow = (index: number, patch: Partial<EditableFilterRow>) => {
    onRowsChange(
      rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row
      )
    );
  };

  const changeField = (index: number, field: string) => {
    const option = fieldOptions.find((candidate) => candidate.field === field);
    if (!option) return;
    const [firstOperatorOption] = option.operatorOptions;
    replaceRow(index, {
      field,
      operator: firstOperatorOption ? firstOperatorOption.operator : 'eq',
      value: initialValueFor(field),
    });
  };

  return (
    <FilterDropdown
      maxHeight={maxHeight}
      title={t(`${keyPrefix}.title`)}
      clearAllLabel={t(`${keyPrefix}.clear_all`)}
      addFilterLabel={t(`${keyPrefix}.add`)}
      noFiltersLabel={t(`${keyPrefix}.none`)}
      noFiltersHintLabel={t(`${keyPrefix}.none_hint`)}
      isEmpty={rows.length === 0}
      onAdd={() => onRowsChange([...rows, newRow()])}
      onClearAll={onClearAll}
      topContent={topContent}
      bottomContent={bottomContent}
    >
      {rows.map((row, index) => (
        <FilterRow
          key={row.id}
          field={row.field}
          operator={row.operator}
          value={row.value}
          connector={row.connector}
          index={index}
          fieldOptions={fieldOptions}
          whereLabel={t(`${keyPrefix}.where`)}
          andLabel={t(`${keyPrefix}.and`)}
          orLabel={t(`${keyPrefix}.or`)}
          removeLabel={t(`${keyPrefix}.remove`)}
          fixedConnectorLabel={fixedConnectorLabelFor(row, index)}
          renderValueInput={valueInputFor(row.field)}
          onFieldChange={changeField}
          onOperatorChange={(rowIndex, operator) =>
            replaceRow(rowIndex, { operator })
          }
          onValueChange={(rowIndex, value) =>
            replaceRow(rowIndex, {
              value: Array.isArray(value) ? value : String(value),
            })
          }
          onConnectorChange={(rowIndex, connector: FilterConnector) =>
            replaceRow(rowIndex, { connector })
          }
          onRemove={(rowIndex) =>
            onRowsChange(
              rows.filter((_, rowPosition) => rowPosition !== rowIndex)
            )
          }
        />
      ))}
    </FilterDropdown>
  );
}
